// src/pages/workflow/AgentToolListPage.tsx
// Manage agent tools — capabilities an agent node may be granted, as data.

import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useWorkflow, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Badge,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import { getEntityId, formatRelativeTime } from '@/features/workflow/logic';
import { agentToolKindMap, agentToolKindOptions } from '@/features/workflow/aiConfig';
import type { AgentTool } from '@core/types/workflow/AgentTool';

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export const AgentToolListPage = observer(() => {
  const store = useWorkflow();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [tools, setTools] = useState<AgentTool[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('all');

  useEffect(() => {
    loadTools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTools = async () => {
    setIsLoading(true);
    try {
      const res = await store.ListAgentTools();
      setTools(res?.agent_tools?.items || []);
    } catch {
      // Error handled by store
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let items = tools;
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(
        (tool) =>
          (tool.name || '').toLowerCase().includes(q) ||
          (tool.slug || '').toLowerCase().includes(q) ||
          (tool.description || '').toLowerCase().includes(q),
      );
    }
    if (kindFilter !== 'all') items = items.filter((tool) => tool.kind === kindFilter);
    return items;
  }, [tools, search, kindFilter]);

  const handleDelete = async (tool: AgentTool) => {
    if (tool.is_builtin) return;
    if (!confirm(t('workflow.deleteAgentToolConfirm'))) return;
    try {
      await store.DeleteAgentTool(getEntityId(tool));
      loadTools();
    } catch {
      // Error handled by store
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/workflows" className="text-muted-foreground hover:text-foreground">{t('workflow.pipelines')}</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('workflow.agentTools')}</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">{t('workflow.agentTools')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('workflow.agentToolsDesc')}</p>
        </div>
        <Button size="sm" onClick={() => navigate('/workflows/agent-tools/new')}>
          <PlusIcon className="h-4 w-4 mr-1.5" />
          {t('workflow.addAgentTool')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <Input
          placeholder={t('workflow.searchAgentTools')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1"
        />
        <Select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
          <option value="all">{t('workflow.allKinds')}</option>
          {agentToolKindOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
          ))}
        </Select>
      </div>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {isLoading ? (
        <Card>
          <CardContent className="p-5">
            <div className="animate-pulse space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-10 bg-muted rounded" />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <svg className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085" />
            </svg>
            <h3 className="text-lg font-medium text-foreground mb-1">{t('workflow.noAgentTools')}</h3>
            <p className="text-sm text-muted-foreground mb-4">{t('workflow.noAgentToolsDesc')}</p>
            <Button onClick={() => navigate('/workflows/agent-tools/new')}>{t('workflow.addAgentTool')}</Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('workflow.field.name')}</TableHead>
                <TableHead>{t('workflow.field.slug')}</TableHead>
                <TableHead>{t('workflow.field.kind')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((tool) => {
                const id = getEntityId(tool);
                const kindInfo = tool.kind ? agentToolKindMap[tool.kind] : undefined;
                return (
                  <TableRow
                    key={id || tool.slug}
                    className="cursor-pointer"
                    onClick={() => navigate(`/workflows/agent-tools/${id}`)}
                  >
                    <TableCell>
                      <div className="font-medium text-foreground">{tool.name}</div>
                      {tool.description && (
                        <div className="text-xs text-muted-foreground line-clamp-1">{tool.description}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-xs font-mono text-muted-foreground">{tool.slug}</span>
                    </TableCell>
                    <TableCell>
                      {kindInfo ? (
                        <Badge variant={kindInfo.variant} size="sm">{t(kindInfo.labelKey)}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {tool.is_active ? (
                          <Badge variant="success" size="sm">{t('workflow.filterActive')}</Badge>
                        ) : (
                          <Badge variant="secondary" size="sm">{t('workflow.filterInactive')}</Badge>
                        )}
                        {tool.is_builtin && <Badge variant="secondary" size="sm">{t('workflow.builtin')}</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <span className="text-xs text-muted-foreground mr-2 hidden sm:inline">
                          {formatRelativeTime(tool.updated_at || tool.created_at)}
                        </span>
                        <span title={tool.is_builtin ? t('workflow.builtinReadOnly') : undefined}>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={tool.is_builtin}
                            onClick={() => navigate(`/workflows/agent-tools/${id}`)}
                          >
                            {t('common.edit')}
                          </Button>
                        </span>
                        <span title={tool.is_builtin ? t('workflow.builtinReadOnly') : undefined}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            disabled={tool.is_builtin}
                            onClick={() => handleDelete(tool)}
                          >
                            {t('common.delete')}
                          </Button>
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
});

export default AgentToolListPage;
